import React, { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Eye, EyeOff, Layers, FileText, ChevronDown, ChevronUp, Activity, Upload, Image } from 'lucide-react';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Toaster, toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTENT_TYPES = ['hero', 'cta', 'section_copy', 'banner', 'featured_item', 'testimonial', 'custom'];
const INTENT_CATEGORIES = ['school-uniforms', 'greek-apparel', 'business-branding', 'personalized-gifts', 'graduations', 'embroidery', 'dtg-printing'];

/* ── Image Upload Field (upload to GCS or paste URL) ── */
const ImageUploadField = ({ value, onChange, token }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API_URL}/api/uploads/admin`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.public_url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-gray-900 text-sm"
          placeholder="Paste image URL or upload below..."
          data-testid="content-field-image"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#4A90A4] text-white text-xs font-semibold rounded-xl hover:bg-[#3a7a94] disabled:opacity-50 transition-all"
          data-testid="upload-image-btn"
        >
          {uploading ? <span className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
      </div>
      {value && (
        <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg border border-gray-100">
          <img src={value} alt="Preview" className="w-16 h-16 rounded-lg object-cover border border-gray-200" onError={(e) => e.target.style.display = 'none'} />
          <p className="text-xs text-gray-500 truncate flex-1">{value}</p>
        </div>
      )}
    </div>
  );
};

const AdminSmartSite = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState('surfaces');
  const [surfaces, setSurfaces] = useState([]);
  const [content, setContent] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Forms
  const [showSurfaceForm, setShowSurfaceForm] = useState(false);
  const [editingSurface, setEditingSurface] = useState(null);
  const [surfaceForm, setSurfaceForm] = useState({ slug: '', label: '', description: '', page: '', active: true });

  const [showContentForm, setShowContentForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [contentForm, setContentForm] = useState({
    surface_slug: '', content_type: 'cta', intent_category: '', sub_intents: [],
    data: {}, sort_order: 0, active: true,
  });
  // Content data as plain fields (no JSON editing)
  const [dataFields, setDataFields] = useState({
    eyebrow:'',highlight:'',subheading:'',body2:'',secondary_text:'',secondary_link:'',headline: '', body: '', button_text: '', button_link: '', image: '', quote: '', author: '', price: '', link: '',
  });

  const headers = { Authorization: `Bearer ${token}` };

  // Which fields to show per content type
  const CONTENT_TYPE_FIELDS = {
    hero: ['eyebrow','headline','highlight','subheading','body','body2','button_text','button_link','secondary_text','secondary_link','image'],
    cta: ['headline', 'body', 'button_text', 'button_link','secondary_text','secondary_link'],
    section_copy: ['eyebrow','headline','body','image'],
    banner: ['headline', 'body', 'button_text', 'button_link', 'image'],
    featured_item: ['headline', 'body', 'image', 'price', 'link'],
    testimonial: ['quote', 'author', 'image'],
    custom: ['headline', 'body', 'button_text', 'button_link', 'image', 'quote', 'author'],
  };

  const FIELD_LABELS = {
    eyebrow:'Small heading',highlight:'Highlighted word',subheading:'Subheading',body2:'Second paragraph',secondary_text:'Second button label',secondary_link:'Second button destination',headline: 'Headline', body: 'Body Text', button_text: 'Button Text', button_link: 'Button Link (URL)',
    image: 'Image URL', quote: 'Quote', author: 'Author', price: 'Price', link: 'Link (URL)',
  };

  const FIELD_TYPES = {
    body2:'textarea',body: 'textarea', quote: 'textarea',
  };

  useEffect(() => { if (token) fetchAll(); }, [token]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sRes, cRes, sessRes] = await Promise.all([
        axios.get(`${API_URL}/api/smart/admin/surfaces`, { headers }),
        axios.get(`${API_URL}/api/smart/admin/content`, { headers }),
        axios.get(`${API_URL}/api/smart/admin/sessions`, { headers }),
      ]);
      setSurfaces(sRes.data);
      setContent(cRes.data);
      setSessions(sessRes.data);
    } catch (err) { toast.error('Failed to load smart site data'); }
    finally { setLoading(false); }
  };

  // ─── Surface CRUD ───
  const saveSurface = async (e) => {
    e.preventDefault();
    try {
      if (editingSurface) {
        await axios.put(`${API_URL}/api/smart/admin/surfaces/${editingSurface}`, surfaceForm, { headers });
        toast.success('Surface updated');
      } else {
        await axios.post(`${API_URL}/api/smart/admin/surfaces`, surfaceForm, { headers });
        toast.success('Surface created');
      }
      resetSurfaceForm();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const deleteSurface = async (id) => {
    if (!window.confirm('Delete this surface?')) return;
    try {
      await axios.delete(`${API_URL}/api/smart/admin/surfaces/${id}`, { headers });
      toast.success('Surface deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const editSurface = (s) => {
    setEditingSurface(s.id);
    setSurfaceForm({ slug: s.slug, label: s.label, description: s.description || '', page: s.page || '', active: s.active });
    setShowSurfaceForm(true);
  };

  const resetSurfaceForm = () => {
    setEditingSurface(null);
    setShowSurfaceForm(false);
    setSurfaceForm({ slug: '', label: '', description: '', page: '', active: true });
  };

  // ─── Content CRUD ───
  const saveContent = async (e) => {
    e.preventDefault();
    // Preserve structured fields while updating visible controls.
    const parsedData = { ...(contentForm.data || {}) };
    Object.entries(dataFields).forEach(([key, value]) => {
      parsedData[key] = value.trim();
      const aliases = {headline:'heading',button_text:'cta_text',button_link:'cta_link',secondary_text:'secondary_cta_text',secondary_link:'secondary_cta_link'};
      if (aliases[key]) parsedData[aliases[key]] = value.trim();
    });

    const payload = { ...contentForm, data: parsedData, sub_intents: contentForm.sub_intents.length ? contentForm.sub_intents : null };
    try {
      if (editingContent) {
        await axios.put(`${API_URL}/api/smart/admin/content/${editingContent}`, payload, { headers });
        toast.success('Content updated');
      } else {
        await axios.post(`${API_URL}/api/smart/admin/content`, payload, { headers });
        toast.success('Content created');
      }
      resetContentForm();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const deleteContent = async (id) => {
    if (!window.confirm('Delete this content variant?')) return;
    try {
      await axios.delete(`${API_URL}/api/smart/admin/content/${id}`, { headers });
      toast.success('Content deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const editContent = (c) => {
    setEditingContent(c.id);
    setContentForm({
      surface_slug: c.surface_slug, content_type: c.content_type,
      intent_category: c.intent_category || '', sub_intents: c.sub_intents || [],
      sort_order: c.sort_order, active: c.active, data: c.data || {},
    });
    // Populate plain text fields from existing data
    const d = c.data || {};
    setDataFields({
      eyebrow:d.eyebrow || '',highlight:d.highlight || '',subheading:d.subheading || '',body2:d.body2 || '',secondary_text:d.secondary_text ?? d.secondary_cta_text ?? '',secondary_link:d.secondary_link ?? d.secondary_cta_link ?? '',headline: d.headline ?? d.heading ?? '', body: d.body || '', button_text: d.button_text ?? (c.surface_slug==='bts-hero' ? 'Shop Uniforms' : d.cta_text) ?? '',
      button_link: d.button_link ?? (c.surface_slug==='bts-hero' ? '/back-to-school#select-school' : d.cta_link) ?? '', image: d.image || '', quote: d.quote || '',
      author: d.author || '', price: d.price || '', link: d.link || '',
    });
    setShowContentForm(true);
  };

  const resetContentForm = () => {
    setEditingContent(null);
    setShowContentForm(false);
    setContentForm({ surface_slug: '', content_type: 'cta', intent_category: '', sub_intents: [], data: {}, sort_order: 0, active: true });
    setDataFields({ eyebrow:'',highlight:'',subheading:'',body2:'',secondary_text:'',secondary_link:'',headline: '', body: '', button_text: '', button_link: '', image: '', quote: '', author: '', price: '', link: '' });
  };

  const inputCls = "w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-gray-900 text-sm";

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-8" data-testid="admin-smart-site-page">
        <h1 className="text-2xl font-bold text-gray-900">Smart Site</h1>
        <p className="text-sm text-gray-500 mt-1">Manage dynamic content surfaces, variants, and view visitor intelligence</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-8 bg-gray-100 rounded-xl p-1 w-fit">
        {[
          { key: 'surfaces', label: 'Surfaces', icon: Layers },
          { key: 'content', label: 'Dynamic Content', icon: FileText },
          { key: 'sessions', label: 'Visitor Sessions', icon: Activity },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              tab === t.key ? 'bg-white text-[#1B3A5C] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* ─── SURFACES TAB ─── */}
      {tab === 'surfaces' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => { resetSurfaceForm(); setShowSurfaceForm(true); }}
              className="inline-flex items-center px-5 py-2.5 bg-[#F5A623] text-gray-900 font-bold text-sm rounded-full btn-glow"
              data-testid="add-surface-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Surface
            </button>
          </div>

          {showSurfaceForm && (
            <div className="mb-6 p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <h3 className="font-bold mb-4 text-gray-900">{editingSurface ? 'Edit Surface' : 'New Surface'}</h3>
              <form onSubmit={saveSurface} className="grid sm:grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium mb-1 text-gray-600">Slug *</label><input type="text" value={surfaceForm.slug} onChange={(e) => setSurfaceForm({ ...surfaceForm, slug: e.target.value })} required className={inputCls} placeholder="homepage-hero" /></div>
                <div><label className="block text-xs font-medium mb-1 text-gray-600">Label *</label><input type="text" value={surfaceForm.label} onChange={(e) => setSurfaceForm({ ...surfaceForm, label: e.target.value })} required className={inputCls} placeholder="Homepage Hero" /></div>
                <div><label className="block text-xs font-medium mb-1 text-gray-600">Description</label><input type="text" value={surfaceForm.description} onChange={(e) => setSurfaceForm({ ...surfaceForm, description: e.target.value })} className={inputCls} /></div>
                <div><label className="block text-xs font-medium mb-1 text-gray-600">Page</label><input type="text" value={surfaceForm.page} onChange={(e) => setSurfaceForm({ ...surfaceForm, page: e.target.value })} className={inputCls} placeholder="home" /></div>
                <div className="sm:col-span-2 flex gap-3 items-center">
                  <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={surfaceForm.active} onChange={(e) => setSurfaceForm({ ...surfaceForm, active: e.target.checked })} className="rounded border-gray-300 text-[#4A90A4]" /><span className="text-sm text-gray-700">Active</span></label>
                  <div className="flex-1" />
                  <button type="submit" className="px-6 py-2 bg-[#4A90A4] text-white font-semibold text-sm rounded-full">{editingSurface ? 'Update' : 'Create'}</button>
                  <button type="button" onClick={resetSurfaceForm} className="px-6 py-2 border border-gray-200 text-gray-600 text-sm rounded-full">Cancel</button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Slug</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Label</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Page</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Content</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {surfaces.map((s) => {
                  const contentCount = content.filter((c) => c.surface_slug === s.slug).length;
                  return (
                    <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 font-mono text-sm text-[#4A90A4]">{s.slug}</td>
                      <td className="px-6 py-4 font-medium text-gray-900">{s.label}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{s.page || '—'}</td>
                      <td className="px-6 py-4"><span className="px-2.5 py-1 bg-[#4A90A4]/10 text-[#4A90A4] text-xs font-semibold rounded-full">{contentCount} variants</span></td>
                      <td className="px-6 py-4">{s.active ? <span className="text-green-600 text-xs font-semibold flex items-center gap-1"><Eye className="w-3 h-3" /> Active</span> : <span className="text-gray-400 text-xs flex items-center gap-1"><EyeOff className="w-3 h-3" /> Inactive</span>}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => editSurface(s)} className="p-2 text-gray-400 hover:text-[#4A90A4] rounded-lg hover:bg-gray-100"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => deleteSurface(s.id)} className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-100"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── CONTENT TAB ─── */}
      {tab === 'content' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => { resetContentForm(); setShowContentForm(true); }}
              className="inline-flex items-center px-5 py-2.5 bg-[#F5A623] text-gray-900 font-bold text-sm rounded-full btn-glow"
              data-testid="add-content-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Content Variant
            </button>
          </div>

          {showContentForm && (
            <div className="mb-6 p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <h3 className="font-bold mb-4 text-gray-900">{editingContent ? 'Edit Content' : 'New Content Variant'}</h3>
              <form onSubmit={saveContent} className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Surface *</label>
                    <select value={contentForm.surface_slug} onChange={(e) => setContentForm({ ...contentForm, surface_slug: e.target.value })} required className={inputCls}>
                      <option value="">Select surface</option>
                      {surfaces.map((s) => <option key={s.slug} value={s.slug}>{s.label} ({s.slug})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Content Type *</label>
                    <select value={contentForm.content_type} onChange={(e) => setContentForm({ ...contentForm, content_type: e.target.value })} className={inputCls}>
                      {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Intent Category</label>
                    <select value={contentForm.intent_category} onChange={(e) => setContentForm({ ...contentForm, intent_category: e.target.value })} className={inputCls}>
                      <option value="">(none — shows for all)</option>
                      {INTENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Sort Order</label>
                    <input type="number" value={contentForm.sort_order} onChange={(e) => setContentForm({ ...contentForm, sort_order: parseInt(e.target.value) || 0 })} className={inputCls} />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={contentForm.active} onChange={(e) => setContentForm({ ...contentForm, active: e.target.checked })} className="rounded border-gray-300 text-[#4A90A4]" /><span className="text-sm text-gray-700">Active</span></label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-3 text-gray-700 uppercase tracking-wide">Content Fields</label>
                  <div className="space-y-3">
                    {(CONTENT_TYPE_FIELDS[contentForm.content_type] || CONTENT_TYPE_FIELDS.custom).filter(field => {
                      const slug=contentForm.surface_slug;
                      if (['eyebrow','highlight','subheading','body2'].includes(field)) return slug==='homepage-hero' || (field==='eyebrow' && slug.startsWith('homepage-'));
                      if (['secondary_text','secondary_link'].includes(field)) return ['homepage-hero','homepage-cta','bts-hero'].includes(slug);
                      if (field==='image' && contentForm.content_type==='section_copy') return slug==='about-history';
                      return true;
                    }).map((field) => (
                      <div key={field}>
                        <label className="block text-xs font-medium mb-1 text-gray-600">{FIELD_LABELS[field] || field}</label>
                        {field === 'image' ? (
                          <ImageUploadField
                            value={dataFields.image || ''}
                            onChange={(url) => setDataFields({ ...dataFields, image: url })}
                            token={token}
                          />
                        ) : FIELD_TYPES[field] === 'textarea' ? (
                          <textarea
                            value={dataFields[field] || ''}
                            onChange={(e) => setDataFields({ ...dataFields, [field]: e.target.value })}
                            rows={3}
                            className={inputCls + ' resize-y'}
                            placeholder={`Enter ${FIELD_LABELS[field]?.toLowerCase() || field}...`}
                            data-testid={`content-field-${field}`}
                          />
                        ) : (
                          <input
                            type="text"
                            value={dataFields[field] || ''}
                            onChange={(e) => setDataFields({ ...dataFields, [field]: e.target.value })}
                            className={inputCls}
                            placeholder={`Enter ${FIELD_LABELS[field]?.toLowerCase() || field}...`}
                            data-testid={`content-field-${field}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="submit" className="px-6 py-2 bg-[#4A90A4] text-white font-semibold text-sm rounded-full">{editingContent ? 'Update' : 'Create'}</button>
                  <button type="button" onClick={resetContentForm} className="px-6 py-2 border border-gray-200 text-gray-600 text-sm rounded-full">Cancel</button>
                </div>
              </form>
            </div>
          )}

          {content.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <p className="text-gray-400 mb-4">No dynamic content yet</p>
              <button onClick={() => setShowContentForm(true)} className="text-[#4A90A4] font-semibold text-sm hover:underline">Create your first content variant</button>
            </div>
          ) : (
            <div className="space-y-3">
              {content.map((c) => (
                <div key={c.id} className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-mono text-xs text-[#4A90A4] bg-[#4A90A4]/10 px-2 py-0.5 rounded">{c.surface_slug}</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{c.content_type}</span>
                        {c.intent_category && <span className="text-xs bg-[#F5A623]/10 text-[#F5A623] px-2 py-0.5 rounded">{c.intent_category}</span>}
                        {c.active ? <span className="text-xs text-green-600">Active</span> : <span className="text-xs text-gray-400">Inactive</span>}
                      </div>
                      <div className="text-sm text-gray-700">
                        {c.data?.headline && <p className="font-semibold">{c.data.headline}</p>}
                        {c.data?.body && <p className="text-gray-500 text-xs mt-1 line-clamp-2">{c.data.body}</p>}
                        {!c.data?.headline && <pre className="text-xs text-gray-400 font-mono line-clamp-2">{JSON.stringify(c.data, null, 2)}</pre>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button onClick={() => editContent(c)} className="p-2 text-gray-400 hover:text-[#4A90A4] rounded-lg hover:bg-gray-100"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => deleteContent(c.id)} className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-100"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── SESSIONS TAB ─── */}
      {tab === 'sessions' && (
        <div>
          {sessions.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-400">No visitor sessions recorded yet</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Session</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Primary Interest</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Pages</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Engagement</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Last Seen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sessions.map((s) => (
                    <tr key={s.session_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 font-mono text-xs text-gray-500">{s.session_id.substring(0, 12)}...</td>
                      <td className="px-6 py-4">{s.primary_interest ? <span className="px-2.5 py-1 bg-[#F5A623]/10 text-[#F5A623] text-xs font-semibold rounded-full">{s.primary_interest}</span> : <span className="text-gray-400 text-xs">—</span>}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{s.pages_viewed_count}</td>
                      <td className="px-6 py-4"><div className="w-16 bg-gray-100 rounded-full h-2"><div className="bg-[#4A90A4] h-2 rounded-full" style={{ width: `${Math.min(100, s.engagement_score * 5)}%` }} /></div></td>
                      <td className="px-6 py-4 text-xs text-gray-500">{s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSmartSite;
